// Which price book items share one picture, and which belong in one price book group (BL-109 v4). Nothing
// names a subject. Items whose words normalise the same are one picture and one group with no call
// (subjectRuleKey). Every other item is judged against the groups of its few closest items (trigram
// similarity on name and category, its own look only), one pair at a time, by Jev (typesafe-ai/jev-1.14.0,
// pinned, through the AI Gateway: a probability), one question a call. Amir, 24 Oct: "We can ask jev
// whether the two items should have the same image and whether they belong in the same price book group."
//
//   - same_picture: would one drawing correctly show both? The approved rules are its words (the lead's
//     live pair test, 24 Oct). The library asks the same of a group and an entry's words.
//   - same_group: do they belong in the same price book group? Kept only as a proposal for the price
//     book's redesign, never applied.
//
// Every question here judges words, so every one is Jev's: these two, the library match and whether a
// subject's words are generic. OpenAI is only the image work (Amir, 25 Oct: "The open AI part is for the
// image generation"): it makes the drawings, and its check looks at the finished picture, which Jev can't
// see (the lead's control test, 24 Oct).
//
// An item joins a group only on a yes against that group's representative (its first member), never
// against any member, so A~B~C can't drift. A group's subject is its representative's normalised name,
// and its picture is drawn from the representative's words, with a few other members' names as context.
//
// What Jev sees: both items' scrubbed words (name, type, category, description; never a price, a code,
// the company's name or a customer), as data. Pure: the worker and the plan use these.
import { similarity } from './pricebook-art-library.ts';
import {
  normaliseSubject,
  plainWords,
  SUBJECT_MAX,
  type ArtAnswer,
  type ArtItem,
  type ArtLook,
  type ArtQuestion,
  type ArtSubject,
} from './pricebook-art-style.ts';

/** The closest items an item is judged against (their groups' representatives), at most. */
export const PAIR_CANDIDATES = 5;
/** The library entries a group's representative is judged against, at most. */
export const LIBRARY_PAIR_CANDIDATES = 5;
/**
 * Where Jev's probabilities count as a yes, each its setting's default. One drawing for two items, a
 * group's representative against a library entry's words and one price book group: 0.6, a yes only when
 * Jev leans clearly yes. Words generic enough for the shared library: 0.8, as sure as a person's request
 * must be (ART_CHECK_DEFAULTS.requestMin), since a wrong yes sends a company's words to every company.
 */
export const SAME_PICTURE_MIN = 0.6; // pricebook.art.jev.samePicture
export const LIBRARY_MATCH_MIN = 0.6; // pricebook.art.jev.libraryMatch
export const SAME_GROUP_MIN = 0.6; // pricebook.art.jev.sameGroup
export const GENERIC_MIN = 0.8; // pricebook.art.jev.generic
/** Jev, pinned (the bare id is listed text-only): every question here that judges words. */
export const JEV_MODEL_DEFAULT = 'typesafe-ai/jev-1.14.0';

const LABEL_WORDS =
  /\b(?:discounts?|fees?|charges?|plans?|memberships?|warrant(?:y|ies)|guarantees?|financing|finance|payments?|coupons?|rebates?|deposits?)\b/i;

/**
 * An item's look: a label for what isn't a thing (a discount always; a fee, charge, plan, membership,
 * warranty or way to pay by its name or category), else a drawing.
 */
export function artLookOf(item: Pick<ArtItem, 'name' | 'type' | 'category'>): ArtLook {
  return item.type === 'discount' || LABEL_WORDS.test(`${item.name} ${item.category ?? ''}`)
    ? 'label'
    : 'drawing';
}

/** A group's subject: its representative's normalised name (the company's name out), and its look. */
export function subjectOfItem(item: ArtItem, companyName?: string | null): ArtSubject {
  const look = artLookOf(item);
  const subject =
    normaliseSubject(plainWords(item.name, companyName, SUBJECT_MAX)) ||
    (look === 'label' ? 'offer' : 'plumbing part');
  return { subject, look };
}

/** An item's words as the judges see them: scrubbed, clipped, never its price or code. */
export function itemWords(item: ArtItem, companyName?: string | null) {
  const category = plainWords(item.category, companyName, 80);
  const description = plainWords(item.description, companyName, 300);
  return {
    name: plainWords(item.name, companyName, 200),
    type: item.type,
    ...(category ? { category } : {}),
    ...(description ? { description } : {}),
  };
}

/**
 * The approved rules for one picture: the lead's live pair test's words (scratchpad/jev/pair.mjs, 24
 * Oct), with the line that the same picture is the same object.
 */
export const SAME_PICTURE_RULES =
  'One drawing per picture. A service that installs, replaces, repairs or services a unit is drawn as that unit. A service with no unit (drain cleaning, camera inspection) is drawn as its machine, never the drain. Different machines are different pictures. Size or capacity matters only if it changes the shape. Discounts, fees, warranties and plans are icon cards: the same offer at a different amount or length shares a card; a different offer or audience does not. The same picture means the same object: a sewer camera and a drain cleaning machine are different pictures, even for similar work.';

/** Jev's question: one drawing for both items (or for a group's representative and a library entry's words). */
export const PICTURE_PAIR_QUESTION: Record<'same_picture', ArtQuestion> = {
  same_picture: {
    type: 'boolean',
    instructions: `Would one drawing correctly show both price book items? ${SAME_PICTURE_RULES}`,
  },
};

/** Jev's question: one price book group for both. */
export const GROUP_PAIR_QUESTION: Record<'same_group', ArtQuestion> = {
  same_group: {
    type: 'boolean',
    instructions:
      'Do the two items belong in the same price book group, the section of the price book an office person would look in for them?',
  },
};

/** Two items as Jev's state: both items' scrubbed words, as one record. */
export function pairState(a: ArtItem, b: ArtItem, companyName?: string | null) {
  return [
    {
      type: 'json' as const,
      value: { item_a: itemWords(a, companyName), item_b: itemWords(b, companyName) },
    },
  ];
}

/**
 * A group's representative against a library entry: the entry is only its stored subject words (never
 * a company's or an item's), as item_b's name.
 */
export function libraryPairState(rep: ArtItem, entrySubject: string, companyName?: string | null) {
  return [
    {
      type: 'json' as const,
      value: {
        item_a: itemWords(rep, companyName),
        item_b: { name: plainWords(entrySubject, null, SUBJECT_MAX) },
      },
    },
  ];
}

/** Before a subject's words join the shared library: are they generic? (Jev's question too) */
export const GENERIC_QUESTION: Record<'generic', ArtQuestion> = {
  generic: {
    type: 'boolean',
    instructions:
      'These words name what a price book picture shows. Are they only a generic description of a thing or an offer, with no brand, maker, product line or model, and no name of a person, place, business or customer?',
  },
};

export function genericState(subject: string, companyName?: string | null) {
  return [{ type: 'json' as const, value: { words: plainWords(subject, companyName, SUBJECT_MAX) } }];
}

const p = (a: ArtAnswer | undefined) => (a && a.type === 'boolean' ? a.probability : null);

/** Jev's yes: its probability at `min` or above. Missing or refused: no. */
export function yes(answers: Partial<Record<string, ArtAnswer>>, q: string, min = 0.5) {
  const v = p(answers[q]);
  return { probability: v, yes: v !== null && v >= min };
}

/** What an item is compared by: its normalised name and category. */
export const pairText = (item: Pick<ArtItem, 'name' | 'category'>) =>
  `${normaliseSubject(item.name)} ${normaliseSubject(item.category)}`.trim();

/**
 * The k closest of `pool` to `item` by trigram similarity on name and category, the same look only,
 * ties in the pool's order: the candidates it's judged against (free; the judging is the call).
 */
export function closestItems<T extends Pick<ArtItem, 'name' | 'type' | 'category'>>(
  item: Pick<ArtItem, 'name' | 'type' | 'category'>,
  pool: readonly T[],
  k = PAIR_CANDIDATES,
): T[] {
  const look = artLookOf(item);
  const text = pairText(item);
  return pool
    .map((c, i) => ({ c, i, score: artLookOf(c) === look ? similarity(text, pairText(c)) : -1 }))
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score || a.i - b.i)
    .slice(0, k)
    .map((x) => x.c);
}
